import {
  Alert, Box, Button, Card, CardContent, Chip, Dialog, DialogActions,
  DialogContent, DialogTitle, Grid, MenuItem, Stack, TextField, Typography,
} from '@mui/material';
import { useMemo, useState } from 'react';
import { Link as RouterLink, useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import ChangeCircleIcon from '@mui/icons-material/ChangeCircle';
import AddIcon from '@mui/icons-material/Add';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

import { PageHeader } from '@/components/PageHeader';
import { DataState } from '@/components/DataState';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { useApiQuery, useApiMutation } from '@/hooks/useApiQuery';
import { useToast } from '@/components/ToastProvider';
import { regOps } from '@/api/endpoints';
import { formatDateTime } from '@/utils/format';
import type { MedicalJurisdiction } from '@/types/medical';

type ChangeRequestStatus = 'submitted' | 'in_review' | 'accepted' | 'rejected' | 'implemented' | 'withdrawn';
type Severity = 'low' | 'medium' | 'high' | 'critical';

type ChangeRequest = {
  id: number;
  jurisdiction: number;
  title: string;
  description: string;
  source: number | null;
  affected_rule_types: string[];
  proposed_body: Record<string, unknown> | null;
  status: ChangeRequestStatus;
  severity: Severity;
  submitted_by: number | null;
  assigned_to: number | null;
  decision_notes: string;
  created_rule: number | null;
  created_release: number | null;
  created_at: string;
  updated_at: string;
};

const STATUS_META: Record<ChangeRequestStatus, { label: string; color: 'default' | 'primary' | 'success' | 'warning' | 'error' }> = {
  submitted:    { label: 'submitted', color: 'warning' },
  in_review:    { label: 'in review', color: 'primary' },
  accepted:     { label: 'accepted',  color: 'success' },
  rejected:     { label: 'rejected',  color: 'error' },
  implemented:  { label: 'implemented', color: 'success' },
  withdrawn:    { label: 'withdrawn', color: 'default' },
};

const SEVERITY_COLOR: Record<Severity, 'default' | 'warning' | 'error'> = {
  low: 'default', medium: 'warning', high: 'error', critical: 'error',
};

// Allowed transitions
const NEXT_STATES: Record<ChangeRequestStatus, ChangeRequestStatus[]> = {
  submitted:   ['in_review', 'rejected', 'withdrawn'],
  in_review:   ['accepted', 'rejected', 'withdrawn'],
  accepted:    ['implemented', 'withdrawn'],
  rejected:    [],
  implemented: [],
  withdrawn:   [],
};

// ─────────────────────────────────────────────────────────────────────────────
// List page
// ─────────────────────────────────────────────────────────────────────────────

export function RegOpsChangeRequestsPage() {
  const [params] = useSearchParams();
  const filterJurisdictionId = params.get('jurisdiction');

  const crsQ = useApiQuery({
    queryKey: ['regops', 'change-requests'],
    queryFn:  regOps.changeRequests.list as () => Promise<ChangeRequest[]>,
  });
  const jurisdictionsQ = useApiQuery({
    queryKey: ['regops', 'jurisdictions'],
    queryFn:  regOps.jurisdictions.list as () => Promise<MedicalJurisdiction[]>,
  });

  const [creating, setCreating] = useState(false);

  const filtered = useMemo(() => {
    const all = crsQ.data ?? [];
    if (!filterJurisdictionId) return all;
    return all.filter((c) => c.jurisdiction === parseInt(filterJurisdictionId, 10));
  }, [crsQ.data, filterJurisdictionId]);

  const jurisdictionMap = useMemo(() => {
    const m = new Map<number, MedicalJurisdiction>();
    (jurisdictionsQ.data ?? []).forEach((j) => m.set(j.id, j));
    return m;
  }, [jurisdictionsQ.data]);

  return (
    <>
      <PageHeader
        title="Change requests"
        subtitle="Pending edits to the rule set, prioritized by severity."
        actions={
          <Button variant="contained" startIcon={<AddIcon />}
                  onClick={() => setCreating(true)}>
            New change request
          </Button>
        }
      />

      <DataState query={crsQ} empty={{ title: 'No change requests' }}>
        {() => filtered.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No change requests for this filter.
          </Typography>
        ) : (
          <Stack spacing={1.5}>
            {filtered
              .slice()
              .sort((a, b) => {
                const sevOrder = { critical: 0, high: 1, medium: 2, low: 3 };
                return (sevOrder[a.severity] ?? 9) - (sevOrder[b.severity] ?? 9);
              })
              .map((c) => (
                <Row key={c.id} cr={c}
                     jurisdictionCode={jurisdictionMap.get(c.jurisdiction)?.code ?? '?'} />
              ))}
          </Stack>
        )}
      </DataState>

      {creating && (
        <CreateDialog
          jurisdictions={jurisdictionsQ.data ?? []}
          onClose={() => setCreating(false)}
        />
      )}
    </>
  );
}

function Row({ cr, jurisdictionCode }: { cr: ChangeRequest; jurisdictionCode: string }) {
  const sm = STATUS_META[cr.status];
  return (
    <Card
      component={RouterLink}
      to={`/regulation-ops/change-requests/${cr.id}`}
      sx={{ textDecoration: 'none', color: 'inherit',
            borderColor: cr.severity === 'critical' || cr.severity === 'high' ? 'error.main' : 'divider',
            '&:hover': { borderColor: 'primary.main' } }}
    >
      <CardContent>
        <Stack direction="row" spacing={2} alignItems="center">
          <Box sx={{
            width: 40, height: 40, borderRadius: 1.5,
            bgcolor: 'background.subtle', color: 'primary.main',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            flexShrink: 0,
          }}>
            <ChangeCircleIcon />
          </Box>
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
              <Typography variant="subtitle1" fontWeight={600} noWrap>
                {cr.title}
              </Typography>
              <Chip size="small" label={jurisdictionCode}
                    variant="outlined" className="tabular" />
              <Chip size="small" label={cr.severity}
                    color={SEVERITY_COLOR[cr.severity]} variant="outlined"
                    sx={{ textTransform: 'capitalize' }} />
              <Chip size="small" label={sm.label} color={sm.color} variant="outlined" />
            </Stack>
            <Typography variant="caption" color="text.secondary">
              {(cr.affected_rule_types ?? []).join(', ') || '—'}
              <span className="tabular"> · {formatDateTime(cr.created_at)}</span>
            </Typography>
          </Box>
          <ChevronRightIcon color="action" />
        </Stack>
      </CardContent>
    </Card>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Detail page
// ─────────────────────────────────────────────────────────────────────────────

export function RegOpsChangeRequestDetailPage() {
  const { id = '' } = useParams<{ id: string }>();
  const numericId = parseInt(id, 10);
  const qc = useQueryClient();
  const toast = useToast();
  const nav = useNavigate();

  const [transition, setTransition] = useState<ChangeRequestStatus | null>(null);

  const q = useApiQuery({
    queryKey: ['regops', 'change-requests', numericId],
    queryFn:  () => regOps.changeRequests.get(numericId) as Promise<ChangeRequest>,
    enabled:  !Number.isNaN(numericId),
  });

  const transitionMut = useApiMutation({
    mutationFn: (vars: { status: ChangeRequestStatus; notes?: string }) =>
      regOps.changeRequests.transition(numericId, vars.status, vars.notes),
    onSuccess: () => {
      toast.success('Status updated.');
      qc.invalidateQueries({ queryKey: ['regops', 'change-requests'] });
      setTransition(null);
    },
    onError: (e) => toast.error(e.message),
  });

  return (
    <>
      <PageHeader
        title="Change request"
        breadcrumbs={[
          { label: 'Change requests', to: '/regulation-ops/change-requests' },
          { label: id },
        ]}
      />

      <DataState query={q}>
        {(cr) => {
          const sm = STATUS_META[cr.status];
          const next = NEXT_STATES[cr.status] ?? [];
          return (
            <Grid container spacing={3}>
              <Grid item xs={12} md={8}>
                <Card sx={{ mb: 3 }}>
                  <CardContent>
                    <Stack direction="row" justifyContent="space-between"
                           alignItems="flex-start" spacing={2} sx={{ mb: 2 }}>
                      <Box>
                        <Typography variant="overline" color="text.secondary">
                          Title
                        </Typography>
                        <Typography variant="h5" component="h2">
                          {cr.title}
                        </Typography>
                      </Box>
                      <Stack alignItems="flex-end" spacing={1}>
                        <Chip label={sm.label} color={sm.color}
                              variant={cr.status === 'accepted' || cr.status === 'implemented' ? 'filled' : 'outlined'} />
                        <Chip size="small" label={cr.severity}
                              color={SEVERITY_COLOR[cr.severity]} variant="outlined"
                              sx={{ textTransform: 'capitalize' }} />
                      </Stack>
                    </Stack>
                    {cr.description && (
                      <>
                        <Typography variant="overline" color="text.secondary"
                                    sx={{ display: 'block', mb: 1 }}>
                          Description
                        </Typography>
                        <Typography variant="body2"
                                    sx={{ whiteSpace: 'pre-wrap', mb: 2 }}>
                          {cr.description}
                        </Typography>
                      </>
                    )}
                    {(cr.affected_rule_types ?? []).length > 0 && (
                      <>
                        <Typography variant="overline" color="text.secondary"
                                    sx={{ display: 'block', mb: 1 }}>
                          Affected rule types
                        </Typography>
                        <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mb: 2 }}>
                          {cr.affected_rule_types.map((t) => (
                            <Chip key={t} size="small" label={t.replace(/_/g, ' ')}
                                  variant="outlined" sx={{ textTransform: 'capitalize' }} />
                          ))}
                        </Stack>
                      </>
                    )}
                    {cr.decision_notes && (
                      <Alert severity="info" variant="outlined">
                        Decision notes: {cr.decision_notes}
                      </Alert>
                    )}
                  </CardContent>
                </Card>

                {cr.proposed_body && Object.keys(cr.proposed_body).length > 0 && (
                  <Card>
                    <CardContent>
                      <Typography variant="overline" color="text.secondary"
                                  sx={{ display: 'block', mb: 1 }}>
                        Proposed rule body
                      </Typography>
                      <Box component="pre"
                           sx={{ p: 2, bgcolor: 'background.subtle', borderRadius: 1.5,
                                 fontFamily: 'ui-monospace, monospace', fontSize: '0.85rem',
                                 whiteSpace: 'pre-wrap', wordBreak: 'break-word', m: 0 }}>
                        {JSON.stringify(cr.proposed_body, null, 2)}
                      </Box>
                    </CardContent>
                  </Card>
                )}
              </Grid>

              <Grid item xs={12} md={4}>
                <Card>
                  <CardContent>
                    <Typography variant="overline" color="text.secondary"
                                sx={{ display: 'block', mb: 1.5 }}>
                      Workflow
                    </Typography>
                    {next.length === 0 ? (
                      <Typography variant="body2" color="text.secondary">
                        Terminal status — no further transitions.
                      </Typography>
                    ) : (
                      <Stack spacing={1}>
                        {next.map((s) => (
                          <Button
                            key={s} fullWidth
                            variant={s === 'rejected' || s === 'withdrawn' ? 'outlined' : 'contained'}
                            color={s === 'rejected' ? 'error' : s === 'accepted' || s === 'implemented' ? 'success' : 'primary'}
                            onClick={() => setTransition(s)}
                          >
                            {STATUS_META[s].label}
                          </Button>
                        ))}
                      </Stack>
                    )}
                    {cr.created_rule && (
                      <Alert severity="success" variant="outlined" sx={{ mt: 2 }}>
                        Implemented by rule #{cr.created_rule}.
                      </Alert>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          );
        }}
      </DataState>

      <ConfirmDialog
        open={transition !== null}
        title={transition ? `Transition to "${STATUS_META[transition].label}"?` : ''}
        description="The new status is recorded with optional notes. Some transitions trigger downstream effects (rejections close the CR; accepted CRs become eligible for implementation)."
        reasonRequired={transition === 'rejected' || transition === 'withdrawn'}
        destructive={transition === 'rejected'}
        confirmLabel={transition ? STATUS_META[transition].label : 'Confirm'}
        onCancel={() => setTransition(null)}
        onConfirm={(notes) =>
          transition
            ? transitionMut.mutateAsync({ status: transition, notes: notes ?? '' })
            : Promise.resolve()
        }
      />

      {/* keep nav lint-clean if route prefetch hooks land later */}
      {void nav}
    </>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Create dialog
// ─────────────────────────────────────────────────────────────────────────────

function CreateDialog({
  jurisdictions, onClose,
}: { jurisdictions: MedicalJurisdiction[]; onClose: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();

  const [jurisdictionId, setJurisdictionId] = useState('');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [severity, setSeverity] = useState<Severity>('medium');
  const [affectedTypes, setAffectedTypes] = useState('');
  const [proposedBody, setProposedBody] = useState('{}');

  let bodyValid = true;
  let parsedBody: Record<string, unknown> = {};
  try { parsedBody = JSON.parse(proposedBody); } catch { bodyValid = false; }

  const createMut = useApiMutation({
    mutationFn: () => regOps.changeRequests.create({
      jurisdiction: parseInt(jurisdictionId, 10),
      title, description, severity,
      affected_rule_types: affectedTypes.split(',').map((s) => s.trim()).filter(Boolean),
      proposed_body: Object.keys(parsedBody).length > 0 ? parsedBody : null,
    }),
    onSuccess: () => {
      toast.success('Change request submitted.');
      qc.invalidateQueries({ queryKey: ['regops', 'change-requests'] });
      onClose();
    },
    onError: (e) => toast.error(e.message),
  });

  const valid = jurisdictionId && title.trim() && bodyValid;

  return (
    <Dialog open onClose={() => !createMut.isPending && onClose()} maxWidth="md" fullWidth>
      <DialogTitle>New change request</DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 0.5 }}>
          <Grid item xs={12} md={6}>
            <TextField fullWidth select required label="Jurisdiction"
                       value={jurisdictionId}
                       onChange={(e) => setJurisdictionId(e.target.value)}>
              {jurisdictions.map((j) => (
                <MenuItem key={j.id} value={String(j.id)}>{j.code}</MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField fullWidth select label="Severity"
                       value={severity}
                       onChange={(e) => setSeverity(e.target.value as Severity)}>
              {(['low', 'medium', 'high', 'critical'] as Severity[]).map((s) => (
                <MenuItem key={s} value={s} sx={{ textTransform: 'capitalize' }}>{s}</MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12}>
            <TextField fullWidth required label="Title"
                       value={title} onChange={(e) => setTitle(e.target.value)} />
          </Grid>
          <Grid item xs={12}>
            <TextField fullWidth multiline minRows={3} label="Description"
                       value={description}
                       onChange={(e) => setDescription(e.target.value)} />
          </Grid>
          <Grid item xs={12}>
            <TextField fullWidth label="Affected rule types"
                       helperText="Comma-separated."
                       value={affectedTypes}
                       onChange={(e) => setAffectedTypes(e.target.value)} />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth multiline minRows={5} label="Proposed body (JSON, optional)"
              error={!bodyValid}
              value={proposedBody}
              onChange={(e) => setProposedBody(e.target.value)}
              sx={{ '& textarea': { fontFamily: 'ui-monospace, monospace', fontSize: '0.875rem' } }}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={createMut.isPending}>Cancel</Button>
        <Button variant="contained"
                disabled={!valid || createMut.isPending}
                onClick={() => createMut.mutate()}>
          Submit
        </Button>
      </DialogActions>
    </Dialog>
  );
}
